import { useAuth0 } from '@auth0/auth0-react';
import {
  AppBar as MuiAppBar,
  AppBarProps as MuiAppBarProps,
  Box,
  styled,
  Theme,
  Toolbar,
  Typography,
  useTheme,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import isNull from 'lodash/isNull';
import { FC, SyntheticEvent, useCallback } from 'react';
import { ReactComponent as Logo } from '../../assets/icons/ox-built-in.svg';
import OxLogoTxt from '../../assets/icons/ox-txt-logo.svg';
import { CategoryRiskNames, IDateRange, important } from 'common-types';
import { isDevelopment } from 'env-utils';
import { useDateRange } from '../api/top-bar-api';
import NonHistoricalDataPopOver from './NonHistoricalDataPopper/NonHistoricalDataPopper';
import DateRangeSelector from './DateRangeSelector/DateRangeSelector';
import Button from '../../elements/Button/Button';
import FeedbackButton from '../../feedback/components/FeedbackButton';
import OrganizationSelectorContainer from '../../organizations/containers/OrganizationSelectorContainer';
import { usePoliciesStore } from '../../policies/api/policies-api';
import FullScanButton from '../../new-scan/containers/FullScanButton';
import LoginButtonContainer from '../containers/LoginButtonContainer';
import AppOwnerSelector from './AppOwnerSelector/AppOwnerSelector';
import OpenDocsButton from './OpenDocsButton';
import ToggleThemeButton from './theme/ToggleThemeButton';
import UserMenu from './user/UserMenu';
import DeltaScanButton from '../../new-scan/containers/ScanButton';
import ExceedScanLimitContainer from '../../new-scan/containers/ExceedScanLimitContainer';
import { useAppOwnersFilterOptions } from '../../application-owners/api/applications-owners-api';
import { setDateRange } from '../store-actions/top-bar-store-actions';
import { useSnapshot } from 'valtio';
import ExecutiveStore from '../../executive-summary/stores/executive-store';
import IssueBadge from '../../common/components/IssueBadge';
import { useIsScanning } from '../../new-scan/actions/scan-actions';
import {
  AppPages,
  currentPage,
  navigate,
  navigateToIssues,
} from '../../common/routing/app-navigator';
import { issuesCountNotExist } from '../../common/common-utils';
import DemoScanButton from '../../new-scan/containers/DemoScanButton';
import ScanStore from '../../new-scan/store/scan-store';

const drawerWidth = 240;
interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppHeader: FC<React.PropsWithChildren<unknown>> = () => {
  const { classes } = useStyles();
  const theme = useTheme();
  const isScanning = useIsScanning();
  const { orgScanInfo, orgScanInfoBreakdownInfo } = useSnapshot(ExecutiveStore);
  const { isAuthenticated, user } = useAuth0();
  const dateRange = useDateRange();
  const appOwnersFilterOptions = useAppOwnersFilterOptions();
  const { enabledPolicies, totalPolicies } = usePoliciesStore();
  const { isDemoEnabled, realScanId } = useSnapshot(ScanStore);
  const disabledPolicies = totalPolicies - enabledPolicies;
  const currPage = currentPage();
  const isIssueCount = issuesCountNotExist(
    orgScanInfoBreakdownInfo?.violations,
  );
  const onDateRangeChange = useCallback((dateRange: IDateRange) => {
    setDateRange(dateRange);
  }, []);

  const onIssueChipClick = useCallback(
    (event: React.MouseEvent<HTMLSpanElement>) => {
      const { name } = event.currentTarget.dataset;
      name && navigateToIssues({ criticality: [CategoryRiskNames[name]] });
    },
    [],
  );

  const onClickOxIcon = useCallback((e: SyntheticEvent<HTMLElement>) => {
    e.preventDefault();
    navigate(AppPages.Dashboard);
  }, []);

  const isOrgAuthenticated = isAuthenticated && user?.org_id;
  if (currPage === AppPages.OrgLogin || currPage === AppPages.OrgSignup) {
    return null;
  }
  return (
    <AppBar
      position='fixed'
      elevation={0}
      sx={{
        overflow: 'auto',
        zIndex: theme.zIndex.drawer + 1,
        background: '#1D164D',
        color: theme.palette.getContrastText('#1D164D'),
      }}>
      <Toolbar
        sx={{
          px: important('8px'),
          gap: 2,
        }}>
        <Button variant='text' onClick={onClickOxIcon}>
          <Box display='flex' alignItems='center' gap='4px' component='span'>
            <Logo style={{ height: 15 }} />
            <img src={OxLogoTxt} alt='OX Security' height={20} />
          </Box>
        </Button>
        {orgScanInfoBreakdownInfo?.violations &&
          !isIssueCount &&
          !isScanning && (
            <>
              <Typography variant='body2' color='text.secondary'>
                Issues:
              </Typography>
              <IssueBadge
                top={3}
                handleClick={onIssueChipClick}
                severitiesAlerts={orgScanInfoBreakdownInfo?.violations}
                textColor={theme.palette.common.white}
              />
            </>
          )}
        <div className={classes.content}>
          <div className={classes.controls}>
            {isOrgAuthenticated && (
              <AppOwnerSelector
                appOwnersFilterOptions={appOwnersFilterOptions}
              />
            )}
            {isOrgAuthenticated && (
              <DateRangeSelector
                onChange={onDateRangeChange}
                filter={dateRange}
              />
            )}
            {isOrgAuthenticated &&
              !isNull(orgScanInfo) &&
              !isNull(orgScanInfoBreakdownInfo) && (
                <NonHistoricalDataPopOver
                  discoveredPolicies={{
                    enabled: enabledPolicies,
                    disabled: disabledPolicies,
                  }}
                  activePoliciesCount={enabledPolicies}
                  orgScanInfo={orgScanInfo}
                />
              )}
            {isAuthenticated && (
              <>
                <OrganizationSelectorContainer />
                <ExceedScanLimitContainer />
                {(realScanId || isDemoEnabled) && isDevelopment() && (
                  <DemoScanButton />
                )}
                {isOrgAuthenticated && !realScanId && !isDemoEnabled && (
                  <DeltaScanButton />
                )}
                {isOrgAuthenticated &&
                  isDevelopment() &&
                  !realScanId &&
                  !isDemoEnabled && <FullScanButton />}

                <FeedbackButton />
                <OpenDocsButton />
              </>
            )}
            <>
              <ToggleThemeButton />
              {isOrgAuthenticated && <UserMenu />}
            </>
          </div>
          {!isAuthenticated && <LoginButtonContainer />}
        </div>
      </Toolbar>
    </AppBar>
  );
};

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: prop => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const useStyles = makeStyles()((theme: Theme) => ({
  content: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    flexGrow: 1,
    justifyContent: 'flex-end',
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    justifyContent: 'flex-end',
    '& .MuiSvgIcon-root': {
      width: '0.95rem',
      height: '0.95rem',
    },
  },
  scanningText: {
    color: `white !important`,
  },
  disabledScan: {
    color: important('rgba(255, 255, 255, 0.3)'),
    backgroundColor: important('rgba(255, 255, 255, 0.12)'),
    border: important('none'),
  },
}));

export default AppHeader;
